import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import type { UserType } from '../types/User';

interface InviteData {
  email: string;
  agencyId: string;
  role: string;
  senderEmail: string;
}

interface UseInvitationsResult {
  sendInvitation: (inviteData: InviteData) => Promise<{ success: boolean; message?: string; error?: string }>;
  fetchUserTypes: () => Promise<{ success: boolean; types?: UserType[]; error?: string }>;
  refreshUserTypes: () => Promise<{ success: boolean; types?: UserType[]; error?: string }>;
  userTypes: UserType[] | null;
  checkUserExists: (email: string) => Promise<{ exists: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export const useInvitations = (): UseInvitationsResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userTypes, setUserTypes] = useState<UserType[] | null>(null);

  const sendInvitation = useCallback(async (inviteData: InviteData) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        const errorMessage = 'Authentication token not found';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/invitations/send`,
        inviteData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      if (response.data) {
        return { success: true, message: 'Invitation sent successfully!' };
      } else {
        const errorMessage = 'Failed to send invitation';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error sending invitation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUserTypes = useCallback(async () => {
    if (userTypes) return { success: true, types: userTypes };

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/user_types`);
      if (response.data && response.data.types) {
        setUserTypes(response.data.types);
        return { success: true, types: response.data.types };
      } else {
        const errorMessage = 'Failed to fetch user types';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error fetching user types';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [userTypes]);

  const refreshUserTypes = useCallback(async () => {
    setUserTypes(null);
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/user_types`);
      if (response.data && response.data.types) {
        setUserTypes(response.data.types);
        return { success: true, types: response.data.types };
      } else {
        const errorMessage = 'Failed to fetch user types';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error fetching user types';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkUserExists = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!email) {
        return { exists: false };
      }

      const response = await axios.get(`${API_BASE_URL}/api/auth/user-exists?email=${email}`);
      return { exists: response.data.exists };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error checking user existence';
      setError(errorMessage);
      return { exists: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Prefetch user types once when the hook is mounted (useful when hook used by provider)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!userTypes) {
        const res = await fetchUserTypes();
        if (!mounted) return;
        if (res.success && res.types) setUserTypes(res.types);
      }
    })();
    return () => { mounted = false; };
  }, [fetchUserTypes, userTypes]);

  return {
    sendInvitation,
    fetchUserTypes,
    refreshUserTypes,
    userTypes,
    checkUserExists,
    isLoading,
    error
  };
};
